import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loader2, ShieldCheck, CreditCard } from "lucide-react";

/**
 * Snabbköp för att låsa upp/köpa CV via Stripe (kort, Apple Pay, Google Pay, Link, Klarna).
 * planId är valfritt; annars används det billigaste kandidatpaketet (1 CV).
 */
export function SwishQuickPay({ planId = null }) {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payingStripe, setPayingStripe] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data: pr } = await api.get("/pricing");
        if (!active) return;
        const plans = pr.candidate_plans || [];
        setPlan((planId && plans.find((p) => p.id === planId)) || plans[0]);
      } catch { /* tyst – fallback visas */ }
      finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [planId]);

  const payWithCard = async () => {
    if (!plan) return;
    setPayingStripe(true);
    try {
      const { data } = await api.post("/payments/stripe/checkout", { plan_id: plan.id, origin_url: window.location.origin });
      if (data.checkout_url) window.location.href = data.checkout_url;
      else throw new Error("no url");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Kunde inte starta betalningen");
      setPayingStripe(false);
    }
  };

  if (loading) return <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  if (!plan) return (
    <div className="rounded-2xl border border-border p-5 text-sm text-muted-foreground" data-testid="swish-quickpay-error">
      Kunde inte ladda betalningen just nu. Ladda om sidan och försök igen.
    </div>
  );

  return (
    <div className="rounded-2xl border border-primary/25 bg-primary/[0.04] p-5 md:p-6" data-testid="swish-quickpay">
      <div className="flex items-center gap-2 font-head font-semibold text-lg mb-1">
        <CreditCard className="h-5 w-5 text-primary" /> Betala direkt – lås upp på sekunden
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Betala {plan.price} kr med kort, Apple Pay, Google Pay, Link eller Klarna – ditt CV låses upp automatiskt direkt efter betalning. Priset är inkl. moms.
      </p>
      <Button className="rounded-full w-full sm:w-auto" onClick={payWithCard} disabled={payingStripe} data-testid="quickpay-card-btn">
        {payingStripe ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CreditCard className="h-4 w-4 mr-1.5" />} Betala {plan.price} kr
      </Button>
      <p className="text-xs text-muted-foreground mt-4 flex items-center gap-1.5">
        <ShieldCheck className="h-3.5 w-3.5 text-primary" /> Säker betalning via Stripe. Du får kvitto direkt.
      </p>
    </div>
  );
}

export default SwishQuickPay;
